import React from 'react';
import { Navigate } from 'react-router-dom';
import { Result, Button } from 'antd';
import { useAppSelector } from '@/hooks/hooks';
import { SYSTEM_ROLE_ADMIN } from '@/constants/systemRole';
import { isAdminSubdomain } from '@/utils/extractProjectSlug';
import { APP_HOST, PUBLIC_APEX } from '@/config/public-host';
import { useStrings } from '@/i18n';
import { commonStrings } from '@/i18n/dictionaries/common';

interface AdminRouteProps {
  children: React.ReactNode;
}

/**
 * WEB-1091 §2/AC5 — where an admin without 2FA goes to enable it, from
 * INSIDE the admin panel screen below. The account's 2FA settings live in
 * the tenant app's Settings → Security tab, which does not exist in this
 * SPA build at all on admin.<apex> (router/index.tsx swaps in
 * ADMIN_PANEL_CHILDREN for the whole tree on that host — see IS_ADMIN_HOST),
 * so this MUST be a real cross-host navigation (window.location.href), not
 * a React Router <Link> — same pattern Login.tsx already uses for
 * isAdminHost-specific behaviour.
 *
 * WEB-2330: кабинет живёт на `APP_HOST` (бой — `app.subster.ai`), а не на
 * корне апекса — корень зарезервирован под лендинг.
 */
function goToSecuritySettings(): void {
  window.location.href = `${window.location.protocol}//${APP_HOST}/settings?tab=security`;
}

/**
 * WEB-471 — gates the /admin branch to systemRole === 'admin' only.
 * WEB-1091 §2/AC5 — additionally requires twoFactorEnabled for that admin.
 *
 * Mirrors ProtectedRoute's isLoading/isAuthenticated handling and re-asserts,
 * on the FE, the same access rule the backend already enforces via
 * RolesGuard (backend/src/auth/guards/roles.guard.ts + @Roles(SystemRole.ADMIN)
 * on the admin/* controllers, which now also throws ADMIN_2FA_REQUIRED when
 * an admin has not enrolled 2FA). This is a UX guard, not a security
 * boundary — the backend remains the source of truth for authorization.
 *
 * Without this FE-side check, an admin without 2FA would still see the panel
 * SHELL render (SYSTEM_ROLE_ADMIN passes), and every single admin/* data
 * fetch inside it would then fail with a raw 403 "ADMIN_2FA_REQUIRED" —
 * a technical string, not something a human reads as instructions. Blocking
 * here instead shows ONE clear, actionable screen up front.
 *
 *  isLoading=true                                                        → render null (spinner handled upstream)
 *  isAuthenticated=false                                                  → redirect to /login
 *  authenticated, systemRole !== 'admin'                                  → deny (see below)
 *  authenticated, systemRole === 'admin', server enforces 2FA, no 2FA     → "enable 2FA" screen (see below)
 *  authenticated, systemRole === 'admin', server does NOT enforce 2FA     → render children (WEB-1091 §6 toggle OFF)
 *  authenticated, systemRole === 'admin', has 2FA                        → render children
 *
 * Non-admin handling depends on the host:
 *  - admin.<apex> (dedicated panel host): show an inline "access denied" — we
 *    CANNOT redirect to a dashboard route because on this host every dashboard
 *    route redirects back to /admin (router "/" → /admin), which would loop.
 *  - any other host: redirect to /funnels (the tenant app).
 *
 * The "admin, but no 2FA yet" screen is shown on EVERY host (not just
 * admin.<apex>) for the same loop-avoidance reason, and because the fix
 * (enable 2FA in Settings) is identical regardless of which host the admin
 * happened to land the panel on.
 */
export function AdminRoute({ children }: AdminRouteProps) {
  const s = useStrings(commonStrings);
  const { isAuthenticated, isLoading, user } = useAppSelector(
    (state) => state.auth,
  );

  if (isLoading) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.systemRole !== SYSTEM_ROLE_ADMIN) {
    if (isAdminSubdomain(window.location.hostname, PUBLIC_APEX)) {
      return (
        <Result
          status="403"
          title={s.adminAccessDeniedTitle}
          subTitle={s.adminAccessDeniedSubtitle}
        />
      );
    }
    return <Navigate to="/funnels" replace />;
  }

  // WEB-1091 §6 (PM decision 2026-08-06): gated on the SERVER's live answer
  // (adminTwoFactorEnforced, RichUserResponseDto), not a locally-guessed
  // condition — the server-side requirement itself sits behind
  // ADMIN_2FA_ENFORCEMENT_ENABLED, OFF by default. If this checked only
  // `!user.twoFactorEnabled` it would show this screen (and block real
  // access) even while the server enforces nothing — exactly the failure
  // mode the PM flagged: an admin walking into a wall for no real reason.
  if (user?.adminTwoFactorEnforced && !user?.twoFactorEnabled) {
    return (
      <Result
        status="warning"
        title={s.adminTwoFactorTitle}
        subTitle={s.adminTwoFactorSubtitle}
        extra={
          <Button type="primary" onClick={goToSecuritySettings}>
            {s.adminEnableTwoFactor}
          </Button>
        }
      />
    );
  }

  return <>{children}</>;
}
